import React from 'react';
import { themes, pageTypeMeta } from './themes';
import { Camera, Heart, Star, Calendar, MapPin, Bookmark } from 'lucide-react';

const MemoryPage = ({ page, pageIndex, totalPages, theme }) => {
  const t = themes[theme] || themes['modern-minimal'];
  const meta = pageTypeMeta[page.pageType] || { label: 'Memory', color: t.accent };

  const renderMilestone = () => {
    const milestoneFields = page.milestoneFields || [];
    return (
      <div className="flex-1 overflow-hidden flex flex-col">
        {/* Date/location fields */}
        <div className="flex gap-2 mb-3 flex-wrap">
          {(page.fields || []).map((field, fi) => (
            <div key={fi} className="flex items-center gap-1.5 bg-white/50 px-2.5 py-1.5 rounded-lg" style={{ border: `1px solid ${t.fieldBorder || t.accent + '20'}` }}>
              {fi === 0 ? <Calendar className="w-2.5 h-2.5" style={{ color: t.accent }} /> : <MapPin className="w-2.5 h-2.5" style={{ color: t.accent }} />}
              <span style={{ fontFamily: t.bodyFont, fontSize: '0.55rem', color: t.subtext }}>{field}:</span>
              <div className="w-16 h-4 rounded" style={{ backgroundColor: t.fieldBg || t.bg, borderBottom: `1px dashed ${t.accent}30` }} />
            </div>
          ))}
        </div>
        {page.content && <p className="mb-2" style={{ fontFamily: t.bodyFont, fontSize: '0.65rem', fontStyle: 'italic', color: t.subtext, lineHeight: 1.5 }}>{page.content}</p>}
        {/* Milestone detail fields */}
        <div className="space-y-2.5 flex-1">
          {milestoneFields.map((mf, mi) => (
            <div key={mi}>
              <div className="flex items-center gap-1.5 mb-1">
                <Star className="w-2 h-2" style={{ color: t.accent }} />
                <span style={{ fontFamily: t.bodyFont, fontSize: '0.6rem', fontWeight: 600, color: t.accent }}>{mf}</span>
              </div>
              <div className="h-8 rounded-lg" style={{ backgroundColor: t.fieldBg || t.bg, border: `1px solid ${t.fieldBorder || t.accent + '15'}` }} />
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderPhoto = () => {
    const slots = page.photoSlots || 2;
    const gridClass = slots === 1 ? 'grid-cols-1' : slots <= 2 ? 'grid-cols-2' : 'grid-cols-2';
    return (
      <div className="flex-1 flex flex-col">
        <div className={`grid ${gridClass} gap-3 flex-1`}>
          {Array.from({ length: slots }).map((_, i) => (
            <div key={i} className="rounded-xl flex flex-col items-center justify-center relative" style={{ backgroundColor: t.fieldBg || t.bg, border: `2px dashed ${t.accent}25`, minHeight: slots === 1 ? '10rem' : '6rem' }}>
              <Camera className="w-6 h-6 mb-1.5" style={{ color: t.accent + '40' }} strokeWidth={1.5} />
              <span style={{ fontFamily: t.bodyFont, fontSize: '0.5rem', color: t.accent + '60' }}>Place photo here</span>
            </div>
          ))}
        </div>
        {/* Caption fields */}
        {(page.fields || []).length > 0 && (
          <div className="mt-3 space-y-1.5">
            {page.fields.map((field, fi) => (
              <div key={fi} className="flex items-center gap-2">
                <span style={{ fontFamily: t.bodyFont, fontSize: '0.55rem', color: t.subtext, minWidth: '3rem' }}>{field}:</span>
                <div className="flex-1 h-4 rounded" style={{ borderBottom: `1px dashed ${t.accent}25` }} />
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderLetter = () => {
    const letterPrompt = page.letterPrompt || '';
    return (
      <div className="flex-1 flex flex-col">
        {letterPrompt && (
          <div className="mb-3 px-3 py-2 rounded-xl" style={{ backgroundColor: t.accentLight || t.accent + '08' }}>
            <p style={{ fontFamily: t.font, fontSize: '0.7rem', fontStyle: 'italic', color: t.accent, lineHeight: 1.5 }}>{letterPrompt}</p>
          </div>
        )}
        <div className="space-y-3.5 flex-1">
          {Array.from({ length: 14 }).map((_, i) => (
            <div key={i} className="h-px w-full" style={{ backgroundColor: t.fieldBorder || t.accent + '12' }} />
          ))}
        </div>
      </div>
    );
  };

  const renderTimeline = () => (
    <div className="flex-1 overflow-hidden relative pl-5">
      <div className="absolute left-2 top-0 bottom-0 w-px" style={{ backgroundColor: t.accent + '30' }} />
      {(page.fields || []).map((field, fi) => (
        <div key={fi} className="relative mb-3 pl-3">
          <div className="absolute -left-[0.65rem] top-1 w-2.5 h-2.5 rounded-full border-2" style={{ borderColor: t.accent, backgroundColor: t.cardBg }} />
          <span style={{ fontFamily: t.bodyFont, fontSize: '0.6rem', fontWeight: 600, color: t.text }}>{field}</span>
          <div className="mt-1 h-5 rounded" style={{ backgroundColor: t.fieldBg || t.bg, border: `1px solid ${t.fieldBorder || t.accent + '15'}` }} />
        </div>
      ))}
    </div>
  );

  const renderKeepsake = () => (
    <div className="flex-1 flex flex-col items-center justify-center text-center px-6">
      <div className="w-full aspect-[4/3] rounded-2xl flex flex-col items-center justify-center mb-3" style={{ backgroundColor: t.fieldBg || t.bg, border: `2px dashed ${t.accent}25` }}>
        <Bookmark className="w-8 h-8 mb-2" style={{ color: t.accent + '35' }} strokeWidth={1.5} />
        <span style={{ fontFamily: t.bodyFont, fontSize: '0.6rem', color: t.accent + '50' }}>Place keepsake, ticket, or memento here</span>
      </div>
      {page.content && <p style={{ fontFamily: t.bodyFont, fontSize: '0.6rem', fontStyle: 'italic', color: t.subtext }}>{page.content}</p>}
    </div>
  );

  const renderFavorites = () => (
    <div className="flex-1 overflow-hidden">
      {page.content && <p className="mb-2" style={{ fontFamily: t.bodyFont, fontSize: '0.65rem', fontStyle: 'italic', color: t.subtext }}>{page.content}</p>}
      <div className="space-y-2">
        {(page.fields || []).map((field, fi) => (
          <div key={fi} className="flex items-center gap-2">
            <Heart className="w-2.5 h-2.5 flex-shrink-0" style={{ color: t.accent }} strokeWidth={1.5} />
            <span style={{ fontFamily: t.bodyFont, fontSize: '0.6rem', fontWeight: 500, color: t.text, minWidth: '5rem' }}>{field}:</span>
            <div className="flex-1 h-5 rounded-lg" style={{ backgroundColor: t.fieldBg || t.bg, border: `1px solid ${t.fieldBorder || t.accent + '15'}` }} />
          </div>
        ))}
      </div>
    </div>
  );

  const renderDefault = () => (
    <div className="flex-1 overflow-hidden">
      {page.content && <p className="mb-3 leading-relaxed" style={{ fontFamily: t.bodyFont, fontSize: '0.7rem', color: t.subtext, lineHeight: 1.7 }}>{page.content}</p>}
      {page.fields && page.fields.length > 0 && (
        <div className="space-y-2">
          {page.fields.map((field, fi) => (
            <div key={fi} className="flex items-center gap-2">
              <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: t.accent }} />
              <span style={{ fontFamily: t.bodyFont, fontSize: '0.65rem', color: t.text, fontWeight: 500, minWidth: '5rem' }}>{field}</span>
              <div className="flex-1 h-6 rounded-lg" style={{ backgroundColor: t.fieldBg || t.bg, border: `1px solid ${t.fieldBorder || t.accent + '15'}` }} />
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const contentMap = {
    'memory-milestone': renderMilestone,
    'memory-photo': renderPhoto,
    'memory-letter': renderLetter,
    'memory-timeline': renderTimeline,
    'memory-keepsake': renderKeepsake,
    'memory-favorites': renderFavorites,
  };

  const render = contentMap[page.pageType] || renderDefault;

  return (
    <div data-testid={`document-page-${pageIndex}`} className="w-full aspect-[210/297] flex flex-col relative overflow-hidden" style={{ backgroundColor: t.cardBg }}>
      <div className="h-1 w-full" style={{ background: `linear-gradient(to right, ${meta.color}40, ${t.accent}40)` }} />
      <div className="px-7 pt-5 pb-3 flex items-start justify-between">
        <div className="flex-1">
          <span className="inline-block text-[8px] font-bold tracking-[0.15em] uppercase px-2 py-0.5 rounded-full mb-2" style={{ fontFamily: t.bodyFont, backgroundColor: meta.color + '15', color: meta.color }}>{meta.label}</span>
          <h2 style={{ fontFamily: t.font, fontWeight: 700, fontSize: 'clamp(1rem, 2.5vw, 1.3rem)', color: t.text, lineHeight: 1.2 }}>{page.title}</h2>
        </div>
        <span style={{ fontFamily: t.bodyFont, fontSize: '0.6rem', color: t.subtext, opacity: 0.5 }}>{pageIndex + 1}/{totalPages}</span>
      </div>
      <div className="px-7 flex-1 overflow-hidden flex flex-col">
        {render()}
      </div>
      <div className="px-7 py-2 flex items-center justify-between" style={{ borderTop: `1px solid ${meta.color}10` }}>
        <span style={{ fontFamily: t.bodyFont, fontSize: '0.55rem', color: t.subtext, opacity: 0.4, letterSpacing: '0.1em' }}>LUMERA STUDIO</span>
        <span style={{ fontFamily: t.bodyFont, fontSize: '0.55rem', color: t.subtext, opacity: 0.4 }}>Page {pageIndex + 1}</span>
      </div>
    </div>
  );
};

export default MemoryPage;
